import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { postApprovers } from '../services/adminContracts';
import { getMonthlyTeams, postMonthlyApprovers } from '../services/monthlyServiceRequest';
import { groupApprovers } from '../utils/formatUtils';

import useToast from './useToast';

export const approverOriginEnum = {
  servicio: 1,
  equipo: 2,
  contrato: 3,
  ubicacion: 4,
  ubicacionContrato: 5
};

const useMonthlyApprovers = ({
  currentApprovers,
  details,
  hideForm,
  fetchApprovers,
  setLocalLoader,
  ubications
}) => {
  const [optionApprover, setOptionApprover] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamsByApprover, setTeamsByApprover] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationContractOptions, setLocationContractOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [contractOptions, setContractOptions] = useState([]);
  const [filteredApprovers, setFilteredApprovers] = useState([]);

  const { setNewToast } = useToast();

  const { id } = useParams();
  const { values, setFieldValue, resetForm } = useFormik({
    initialValues: {
      approver: null,
      team: null,
      service: null,
      contract: null,
      location: null,
      locationContract: null
    }
  });

  const getTeamsByApprover = (approvers) => {
    // Build teams for each approver with those whose origenEnum === 2
    const teamOptionsByApprover = {};

    groupApprovers(approvers)?.forEach((apprList) => {
      const approverTeamOptions = apprList
        .filter(
          (approver) => approver.origenEnum === approverOriginEnum.equipo && approver.origenStr
        )
        .map((appr, index) => ({
          id: index,
          value: index,
          nombre: appr.origenStr,
          label: appr.origenStr
        }));
      const approverId = apprList[0].id;

      teamOptionsByApprover[approverId] = approverTeamOptions;
    });
    setTeamsByApprover(teamOptionsByApprover);
  };

  const formatApproversAndTeams = (approvers) => {
    const formattedApprovers = approvers?.filter(
      (approver, index) => approvers.findIndex((appr) => appr.id === approver.id) === index
    );

    getTeamsByApprover(approvers);

    return formattedApprovers;
  };
  // idEvento remains pending for the next story — for now disable the add approvers button
  const getApproversByContract = async () => {
    try {
      const teamsList = await getMonthlyTeams(id);
      const body = details?.unidadNegocio?.map((uunn, index) => ({
        nroContrato: details?.numeroContrato,
        idUnidadDeNegocio: uunn.id,
        idEvento: ubications[index]?.idEvento,
        idTipoNegocio: 1
      }));

      const newBody = {
        contratosEventos: body,
        idServicio: details?.servicio?.id,
        idEquipos: teamsList !== null ? teamsList : []
      };

      // Here postApproversContracts was replaced by postApprovers
      const response = await postApprovers(newBody);

      return response;
    } catch (_) {
      return [];
    }
  };

  const getApproversbyContractOyM = async () => {
    try {
      const body = details?.operacion?.map((operacion) => ({
        nroContrato: details?.numeroContrato,
        idUnidadDeNegocio: operacion.id,
        idTipoNegocio: 2,
        idEvento: 0
      }));

      const newBody = {
        contratosEventos: body,
        idServicio: details?.servicio?.id,
        idEquipos: [],
        idUbicacionesTecnicas: ubications.map((ubi) => ubi.idUbicacionTecnica)
      };

      // Here postApproverContractOyM was replaced by postApprovers
      const response = await postApprovers(newBody);

      return response;
    } catch (_) {
      return [];
    }
  };

  const getApprovers = async () => {
    setLocalLoader(true);
    let approvers = [];

    if (details?.numeroContrato && details?.unidadNegocio?.length) {
      const approversByContract = await getApproversByContract();

      approvers = [...approversByContract];
    }

    if (details?.operacion?.length) {
      const approversbyContractOyM = await getApproversbyContractOyM();

      approvers = [...approversbyContractOyM];
    }

    setFilteredApprovers(approvers); // used in formatInputApprovers to show input options by origin

    approvers = formatApproversAndTeams(approvers); // ensures the same approver does not repeat in the approvers input
    setOptionApprover(approvers);
    setLocalLoader(false);
  };

  const formatInputApprovers = (originEnum) => {
    const formattedInput = filteredApprovers
      .filter(
        (approver) => approver.id === values?.approver?.id && approver.origenEnum === originEnum
      )
      .map((option) => ({
        label: option.origenStr,
        value: option.origenStr,
        nombre: option.origenStr,
        id: option.origenId
      }));

    return formattedInput;
  };

  // Teams
  const getTowerEquipmentOptions = () => {
    const optionsTeams = formatInputApprovers(approverOriginEnum.equipo);

    if (optionsTeams.length) {
      setTeamOptions(optionsTeams);
    } else {
      setTeamOptions([]);
    }
  };

  // Ubications
  const getLocationOptions = () => {
    const optionsLocations = formatInputApprovers(approverOriginEnum.ubicacion);

    if (optionsLocations.length) {
      setLocationOptions(optionsLocations);
    } else {
      setLocationOptions([]);
    }
  };

  // Ubication and contract
  const getLocationContracts = () => {
    const optionsLocationsContract = formatInputApprovers(approverOriginEnum.ubicacionContrato);

    if (optionsLocationsContract.length) {
      setLocationContractOptions(optionsLocationsContract);
    } else {
      setLocationContractOptions([]);
    }
  };

  // Services
  const getServiceOptions = () => {
    const optionsService = formatInputApprovers(approverOriginEnum.servicio);

    if (optionsService.length) {
      setServiceOptions(optionsService);
    } else {
      setServiceOptions([]);
    }
  };

  // Contracts
  const getContractOptions = () => {
    const optionsContract = formatInputApprovers(approverOriginEnum.contrato);

    if (optionsContract.length) {
      setContractOptions(optionsContract);
    } else {
      setContractOptions([]);
    }
  };

  const approverExists = () =>
    currentApprovers.some(
      (approver) =>
        approver.nombre === values.approver?.nombre &&
        approver.ubicacionTecnicaNombre === (values.location?.nombre || null) &&
        approver.ubicacionContrato === (values.locationContract?.nombre || null) &&
        approver.equipoNombre === (values.team?.nombre || null) &&
        approver.servicioNombre === (values.service?.nombre || null) &&
        approver.contratoNumero === (Number(values.contract?.nombre) || null)
    );
  const addApprover = async () => {
    setLocalLoader(true);
    try {
      const exist = approverExists();

      if (exist) {
        throw new Error('El aprobador ya existe');
      }

      const body = {
        idSolicitudServicioMensualizada: id || null,
        nombre: values.approver.nombre || null,
        idAprobador: values.approver.id || null,
        esGrupo: !!values.approver?.esGrupo,
        equipoNombre: values.team?.nombre || null,
        ubicacionTecnicaNombre: values.location?.nombre || null,
        ubicacionContrato: values.locationContract?.nombre || null,
        servicioNombre: values.service?.nombre || null,
        contratoNumero: values.contract?.nombre || null,
        idServicio: values.service?.id || null,
        idEquipo: values.team?.id || null,
        idConfiguracionContrato: values.contract?.id || null,
        idUbicacionTecnica: values.location?.id || values.locationContract?.id || null
      };

      await postMonthlyApprovers(body);
      await fetchApprovers(true);
      hideForm();
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
    setLocalLoader(false);
  };

  useEffect(() => {
    if (values.approver) {
      getServiceOptions();
      getLocationOptions();
      getContractOptions();
      getLocationContracts();
      getTowerEquipmentOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.approver]);

  useEffect(() => {
    getApprovers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  return {
    optionApprover,
    teamOptions,
    teamsByApprover,
    locationOptions,
    locationContractOptions,
    serviceOptions,
    contractOptions,
    values,
    setFieldValue,
    resetForm,
    addApprover
  };
};

export default useMonthlyApprovers;
